import { useTranslation } from 'react-i18next';
import { Mountain } from 'lucide-react';
import { useDndContext, useDroppable } from '@dnd-kit/core';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import type { CragRouteSector } from '../../../types/crag-route';
import { sectorDropId } from './drop-targets';

interface SectorColumnProps {
  sectors: CragRouteSector[];
  selectedId: string | null;
  onSelect: (sectorId: string) => void;
  loading?: boolean;
}

/**
 * The crag's sectors — the first column, and the first choice: every other
 * column is about one cliff at a time.
 *
 * Each row carries the sector's photo, because that photo is what the editor
 * draws on: a sector with none can be picked but has nothing to draw against,
 * and the thumbnail is what says so before it is opened.
 *
 * Each row is also where routes are dropped to be moved here — one picked up by
 * its handle in the column to the right, or the whole selection. That is the
 * only way a route changes sector in this panel, so the rows say so as soon as
 * a drag starts rather than waiting to be hovered.
 */
export function SectorColumn({ sectors, selectedId, onSelect, loading }: SectorColumnProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="space-y-2 p-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (sectors.length === 0) {
    return <p className="p-3 text-sm text-muted-foreground">{t('cragAdmin.routes.noSectors')}</p>;
  }

  return (
    <ul className="space-y-1 p-2">
      {sectors.map((sector) => (
        <SectorRow
          key={sector.id}
          sector={sector}
          selected={sector.id === selectedId}
          onSelect={onSelect}
        />
      ))}
    </ul>
  );
}

function SectorRow({
  sector,
  selected,
  onSelect,
}: {
  sector: CragRouteSector;
  selected: boolean;
  onSelect: (sectorId: string) => void;
}) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: sectorDropId(sector.id) });
  // Whether anything is being dragged at all, read off the shared context rather
  // than threaded down from the page: the rows only need to know that a drop is
  // possible, and this column is always rendered inside that context.
  const { active } = useDndContext();

  return (
    <li ref={setNodeRef}>
      <button
        type="button"
        onClick={() => onSelect(sector.id)}
        aria-current={selected}
        data-testid="crag-sector-row"
        className={cn(
          'flex w-full items-center gap-2 rounded-md border p-2 text-left transition-colors hover:bg-accent',
          selected && 'border-primary bg-accent',
          active !== null && 'border-dashed',
          isOver && 'border-solid border-primary bg-primary/10 ring-2 ring-primary'
        )}
      >
        {sector.image_url ? (
          <img
            src={sector.image_url}
            alt=""
            className="size-9 shrink-0 rounded object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span className="flex size-9 shrink-0 items-center justify-center rounded bg-muted">
            <Mountain className="size-4 text-muted-foreground" aria-hidden />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{sector.name}</span>
          <span className="block text-xs text-muted-foreground">
            {active !== null
              ? t('cragAdmin.routes.moveHere')
              : t('cragAdmin.routes.routeCount', { count: sector.routes_count })}
          </span>
        </span>
      </button>
    </li>
  );
}
